// # 묶음 조회 캐시 — "이 키들이 보인다" 를 모아 한 번에 묻는다
//
// 첨부(`GET /v1/attachments/{id}`)와 링크 카드(`POST /v1/unfurl`)가 같은 모양이다:
// 화면에 보이는 것만, 모르는 것만, 한 번에. 반응(`reactions.ts`)도 비슷하지만
// 채널 단위 사건이 있어 따로 둔다.
//
// ## 값에 수명이 있다
// 첨부의 서명 주소는 수명이 있다. 오래 들고 있으면 누른 순간 403 이 난다. 그래서 항목마다
// `expires` 를 적고, 다시 `need` 될 때 지난 것은 다시 묻는다. 화면에 계속 보이는
// 줄이면 `need` 가 계속 불리므로 저절로 갱신된다.
//
// 다시 물어 **같은 값**이 오면 알리지 않는다. 알리면 판 전체가 새 `Map` 을 받아 줄마다
// 다시 그리고, `<img src>` 가 같아도 그 비용은 남는다. 바뀐 값만 새 객체로 바꾼다.

export type Found<V> = ReadonlyMap<string, V | null>

export interface LookupConfig<V> {
  /** 한 번에 묻는다. 답에 없는 키는 "없다" 로 적는다. */
  fetch_many(keys: readonly string[]): Promise<ReadonlyMap<string, V>>
  /** 이 시간이 지나면 다시 묻는다. 없으면 영원히 기억한다. */
  ttl_ms?: number
  /** 한 요청에 담는 최대 키 수. */
  batch_max?: number
  now?: () => number
}

interface Entry<V> {
  value: V | null
  expires: number
}

export class Lookup<V> {
  #config: LookupConfig<V>
  #have = new Map<string, Entry<V>>()
  #wanted = new Set<string>()
  #asking: Promise<void> | null = null
  #listeners = new Set<() => void>()
  #view: Found<V> = new Map()

  constructor(config: LookupConfig<V>) {
    this.#config = config
  }

  subscribe = (listen: () => void): (() => void) => {
    this.#listeners.add(listen)
    return () => void this.#listeners.delete(listen)
  }

  /** 같은 상태면 같은 객체. 만료된 값도 **그대로 보인다** — 새 답이 올 때 바뀐다. */
  slice = (): Found<V> => this.#view

  need(keys: readonly string[]): void {
    const now = this.#config.now?.() ?? Date.now()
    let added = false
    for (const k of keys) {
      const e = this.#have.get(k)
      if ((e !== undefined && e.expires > now) || this.#wanted.has(k)) continue
      this.#wanted.add(k)
      added = true
    }
    if (added) this.#ask()
  }

  /** 하나를 잊는다(지웠거나 틀렸다). 다음 `need` 가 다시 묻는다. */
  forget(key: string): void {
    if (this.#have.delete(key)) this.#publish()
  }

  #ask(): void {
    if (this.#asking !== null) return
    this.#asking = Promise.resolve().then(async () => {
      try {
        while (this.#wanted.size > 0) {
          const keys = [...this.#wanted].slice(0, this.#config.batch_max ?? 50)
          for (const k of keys) this.#wanted.delete(k)
          const got = await this.#config.fetch_many(keys)
          const expires = (this.#config.now?.() ?? Date.now()) + (this.#config.ttl_ms ?? Infinity)
          let changed = false
          for (const k of keys) {
            const value = got.get(k) ?? null
            const was = this.#have.get(k)
            const same = was !== undefined && same_value(was.value, value)
            this.#have.set(k, { value: same ? was.value : value, expires })
            changed ||= !same
          }
          if (changed) this.#publish()
        }
      } catch {
        // 다음 `need` 가 다시 묻는다. 카드나 첨부 때문에 화면에 오류를 띄우지 않는다.
        this.#wanted.clear()
      } finally {
        this.#asking = null
      }
    })
  }

  #publish(): void {
    const next = new Map<string, V | null>()
    for (const [k, e] of this.#have) next.set(k, e.value)
    this.#view = next
    for (const l of this.#listeners) l()
  }
}

/** 답은 JSON 에서 온 작은 객체다. 모양이 같으면 같은 값이다. */
function same_value(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b)
}
